import { useState, useEffect } from 'react';

const DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in ms

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
};

function getTimeLeft(startMs: number): TimeLeft {
  const endMs = startMs + DURATION_MS;
  const nowMs = Date.now();
  const diff = Math.max(0, endMs - nowMs);

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, totalMs: diff };
}

const CountdownTimer = () => {
  const [startMs, setStartMs] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalMs: DURATION_MS,
  });

  useEffect(() => {
    // Determine start date
    const params = new URL(window.location.href).searchParams;
    const forceYesterday = params.get('sim') === 'yesterday';

    const stored = localStorage.getItem('countdown-start-date');
    let start: number;

    if (forceYesterday || !stored) {
      // Assume it started yesterday for first-time visitors or when forced via URL param
      start = Date.now() - 24 * 60 * 60 * 1000;
      localStorage.setItem('countdown-start-date', new Date(start).toISOString());
    } else {
      start = new Date(stored).getTime();
    }

    setStartMs(start);
    setTimeLeft(getTimeLeft(start));

    // Update every second
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(start));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isDone = timeLeft.totalMs <= 0;

  return (
    <section className="text-center py-12 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl border border-primary/10" aria-live="polite">
      <h2 className="text-2xl font-semibold text-foreground mb-8">
        {isDone ? 'Financial Statements Are Available' : 'Financial Statements Will Be Available'}
      </h2>
      <div className="inline-flex items-center justify-center">
        <div className="bg-primary/10 rounded-2xl p-8 border border-primary/20">
          {!isDone ? (
            <>
              <div className="grid grid-cols-4 gap-6">
                {['Days', 'Hours', 'Minutes', 'Seconds'].map((label, idx) => {
                  const value =
                    idx === 0
                      ? timeLeft.days
                      : idx === 1
                      ? timeLeft.hours
                      : idx === 2
                      ? timeLeft.minutes
                      : timeLeft.seconds;
                  return (
                    <div key={label} className="min-w-[72px]">
                      <div className="text-5xl md:text-6xl lg:text-7xl font-bold text-primary mb-2">
                        {value.toString().padStart(2, '0')}
                      </div>
                      <div className="text-sm font-medium text-muted-foreground">{label}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-xl font-medium text-primary">Available now</div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CountdownTimer;